import * as React from "react";
import { useState, useEffect, useContext } from "react";
import { observer } from "mobx-react";
import Link from "next/link";
import { useRouter } from "next/navigation";

import PageHeader from "../../page-header";
import { NextSeo } from "next-seo";
import { Tab, Tabs } from "@blueprintjs/core";
import { CellBioDetailsOverviewPanel } from "./overview";
import { CellBioDetailsQCPanel } from "./qc";
import { StoresContext } from "../../../pages/_app";
import Loading from "../../loading";
import UnexpectedBackendError from "../../backend-error";
import { Genome } from "../../../api";
import { GenomeHeader } from "../../genome-header";
import { getGenomeSeo } from "../../../utils/genomes";

type TabType = "overview" | "quality_control";
const DEFAULT_TAB: TabType = "overview";
function getTabFromUrl(tab?: string | null): TabType {
  switch (tab) {
    case "quality_control":
      return "quality_control";
    default:
      return DEFAULT_TAB;
  }
}

const CellBioDetailsPage = observer(({ uuid, tab: tabInUrl }: { uuid: string; tab?: string }) => {
  const router = useRouter();
  const rootStore = useContext(StoresContext);
  const { detailsData } = rootStore.cellBioStore;
  const { genome } = detailsData;

  const [currentTab, setCurrentTab] = useState<TabType>(() => getTabFromUrl(tabInUrl));

  useEffect(() => {
    rootStore.cellBioStore.fetchDetailsIfNeeded(uuid);
  }, [uuid, detailsData.loading, detailsData.genome?.uuid]);

  useEffect(() => {
    return () => {
      rootStore.cellBioStore.resetDetailsData();
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentTab(getTabFromUrl(new URLSearchParams(window.location.search).get("tab")));
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const handleChangeTab = (newTabId: TabType, prevTabId: TabType) => {
    if (newTabId === prevTabId) {
      return;
    }

    setCurrentTab(newTabId);
    const newSearchParams = new URLSearchParams();
    newSearchParams.set("tab", newTabId);
    const newUrl = `/cell-bio/${uuid}?${newSearchParams.toString()}`;
    router.push(newUrl);
  };

  if (detailsData.error) {
    return <UnexpectedBackendError />;
  }
  if (detailsData.loading || !genome) {
    return <Loading text="Loading genome..." />;
  }

  const [collectionUrl, collectionLabel] = collectionNameUrlAndLabel(genome.collection_name);
  const taxaUrlParams = new URLSearchParams();
  taxaUrlParams.set("taxonomy", genome.taxon.name);

  return (
    <>
      <NextSeo {...getGenomeSeo(genome)} />
      <PageHeader
        name={
          <GenomeHeader
            genome={{
              external_url: genome.external_url,
              taxon_name: genome.attributes.atcc_metadata?.cell_line_name || genome.taxon_name,
              name: genome.name,
            }}
          />
        }
      >
        <div className="breadcrumbs">
          <ul className="bp4-breadcrumbs inner-breadcrumbs">
            <li>
              <Link className="bp4-breadcrumb" href="/cell-bio">
                All Cell Bio Products
              </Link>
            </li>
            <li>
              <Link className="bp4-breadcrumb" href={collectionUrl}>
                {collectionLabel}
              </Link>
            </li>
            <li>
              <Link
                className="bp4-breadcrumb bp4-breadcrumb-current"
                href={collectionUrl + "?" + taxaUrlParams.toString()}
              >
                {genome.taxon.name}
              </Link>
            </li>
          </ul>
        </div>
      </PageHeader>

      <div className="detail-navigation">
        <Tabs id="tabs" animate={false} selectedTabId={currentTab} onChange={handleChangeTab}>
          <Tab
            title="Overview"
            id={"overview" as TabType}
            panel={<CellBioDetailsOverviewPanel genome={genome} />}
          />
          <Tab
            title="Quality Control"
            id={"quality_control" as TabType}
            panel={<CellBioDetailsQCPanel genome={genome} />}
          />
        </Tabs>
      </div>
    </>
  );
});

function collectionNameUrlAndLabel(collectionName: Genome["collection_name"]): [string, string] {
  const url = `/cell-bio/${collectionName.replace("_", "-")}`;

  switch (collectionName) {
    case "animal_cells":
      return [url, "Animal Cell Lines"];
    case "human_cells":
      return [url, "Human Cell Lines"];
    case "organoids":
      return [url, "Organoids Lines"];
  }

  return [url, "Cell Lines"];
}

export default CellBioDetailsPage;
